import React, { Fragment, useEffect, useMemo, useState } from 'react';

import Field from '@atlaskit/form/field';
import type { ValueType } from '@atlaskit/select/types';
import Select from '@atlaskit/select/select';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';

import type { AvailableSite } from '@atlassian/gic-anywhere/utils';

interface AvailableSiteOption {
	label: string;
	value: AvailableSite;
}

export const mapSiteToOption = (site: AvailableSite): AvailableSiteOption => ({
	label: site.displayName,
	value: site,
});

interface SiteSelectorProps {
	options: AvailableSite[];
	value: AvailableSite | null;
	onChange: (site: AvailableSite | null) => void;
	isLoading: boolean;
	isDisabled: boolean;
	isRequired?: boolean;
	label: string;
}

export const SiteSelector: React.FC<SiteSelectorProps> = (props) => {
	const {
		options = [],
		value: controlledValue,
		onChange,
		isLoading = false,
		isDisabled = false,
		label,
	} = props;

	const [localValue, setLocalValue] = useState<AvailableSite | null>(controlledValue ?? null);
	const mappedOptions = useMemo(() => options?.map(mapSiteToOption), [options]);
	const selectedValue = localValue ? mapSiteToOption(localValue) : null;

	// Keep local state in sync with controlled value
	useEffect(() => {
		if (controlledValue !== undefined) {
			setLocalValue(controlledValue);
		}
	}, [controlledValue, setLocalValue]);

	const handleChange = (selectedOption: ValueType<AvailableSiteOption>) => {
		if (!selectedOption || !selectedOption.value) {
			return;
		}

		if (selectedOption.value === localValue) {
			return;
		}

		const newValue = selectedOption ? selectedOption.value : null;
		setLocalValue(newValue);
		onChange?.(newValue);
	};

	return (
		<Field<ValueType<AvailableSiteOption>>
			name="sites"
			label={label}
			testId="work-create-site-field"
			isRequired={props.isRequired ?? false}
		>
			{({ fieldProps: { id, ...rest } }) => (
				<Fragment>
					<AnalyticsContext data={{ attributes: { fieldName: 'workCreateFormSiteSelector' } }}>
						<Select<AvailableSiteOption>
							inputId={id}
							{...rest}
							value={selectedValue}
							options={mappedOptions}
							onChange={handleChange}
							isLoading={isLoading}
							isDisabled={isDisabled || isLoading}
						/>
					</AnalyticsContext>
				</Fragment>
			)}
		</Field>
	);
};
